import React, { useState } from "react";
import type { Client, Expense, Service } from "../../types";
import { useBilling } from "../../store/BillingContext";

export const EntityModal: React.FC = () => {
  const { activeForm, setActiveForm, saveClient, saveService, saveExpense } = useBilling();

  if (!activeForm) return null;

  const { kind, value } = activeForm;
  const [formState, setFormState] = useState<any>(() => structuredClone(value));

  const update = (field: string, val: any) => {
    setFormState((prev: any) => ({ ...prev, [field]: val }));
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (kind === "client") {
      saveClient(formState as Client);
    } else if (kind === "service") {
      saveService(formState as Service);
    } else if (kind === "expense") {
      saveExpense(formState as Expense);
    }
  };

  const isEditing = Boolean(formState.id);
  const titles = {
    client: `${isEditing ? "Edit" : "Add"} Client`,
    service: `${isEditing ? "Edit" : "Add"} Service & Rate`,
    expense: `${isEditing ? "Edit" : "Record"} Expense`,
  };

  return (
    <div className="overlay">
      <div className="modal small">
        <form onSubmit={handleSave}>
          <div className="modal-head">
            <h2>{titles[kind]}</h2>
            <button type="button" onClick={() => setActiveForm(null)}>
              ×
            </button>
          </div>

          <div className="modal-body form-grid">
            {kind === "client" && (
              <>
                <label>
                  <span>Company / Organization Name</span>
                  <input
                    type="text"
                    value={formState.company || ""}
                    placeholder="e.g. Surya Speciality Pharma"
                    onChange={(e) => update("company", e.target.value)}
                    required
                  />
                </label>

                <label>
                  <span>Primary Contact Name</span>
                  <input
                    type="text"
                    value={formState.name || ""}
                    placeholder="e.g. Suresh T"
                    onChange={(e) => update("name", e.target.value)}
                    required
                  />
                </label>

                <label>
                  <span>Email Address</span>
                  <input
                    type="email"
                    value={formState.email || ""}
                    placeholder="billing@company.com"
                    onChange={(e) => update("email", e.target.value)}
                  />
                </label>

                <label>
                  <span>Phone Number</span>
                  <input
                    type="text"
                    value={formState.phone || ""}
                    placeholder="+91 98865 63105"
                    onChange={(e) => update("phone", e.target.value)}
                  />
                </label>

                <label>
                  <span>GSTIN Number</span>
                  <input
                    type="text"
                    value={formState.gstin || ""}
                    placeholder="29AAAAA0000A1Z5"
                    onChange={(e) => update("gstin", e.target.value)}
                  />
                </label>

                <label>
                  <span>Industry / Vertical</span>
                  <input
                    type="text"
                    value={formState.industry || ""}
                    placeholder="e.g. Healthcare, Wellness, Tech"
                    onChange={(e) => update("industry", e.target.value)}
                  />
                </label>

                <label className="wide">
                  <span>Registered Billing Address</span>
                  <textarea
                    value={formState.address || ""}
                    rows={2}
                    placeholder="Complete address for invoices"
                    onChange={(e) => update("address", e.target.value)}
                  />
                </label>
              </>
            )}

            {kind === "service" && (
              <>
                <label>
                  <span>Service Name</span>
                  <input
                    type="text"
                    value={formState.name || ""}
                    placeholder="e.g. Standard Meta Ads Package"
                    onChange={(e) => update("name", e.target.value)}
                    required
                  />
                </label>

                <label>
                  <span>Category</span>
                  <select
                    value={formState.category || "Website Development"}
                    onChange={(e) => update("category", e.target.value)}
                  >
                    <option value="Website Development">Website Development</option>
                    <option value="Meta Ads Management">Meta Ads Management</option>
                    <option value="Growth Combo Packages">Growth Combo Packages</option>
                    <option value="Social Media Management">Social Media Management</option>
                    <option value="SEO & Content">SEO & Content</option>
                    <option value="Custom Engineering">Custom Engineering</option>
                  </select>
                </label>

                <label>
                  <span>Base Rate (₹)</span>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={formState.rate || 0}
                    onChange={(e) => update("rate", Number(e.target.value))}
                    required
                  />
                </label>

                <label>
                  <span>Billing Frequency</span>
                  <select
                    value={formState.billing || "monthly"}
                    onChange={(e) => update("billing", e.target.value)}
                  >
                    <option value="monthly">Monthly Retainer</option>
                    <option value="one_time">One-Time Project</option>
                  </select>
                </label>

                <label>
                  <span>HSN / SAC Code</span>
                  <input
                    type="text"
                    value={formState.hsn || "998361"}
                    onChange={(e) => update("hsn", e.target.value)}
                  />
                </label>

                <label>
                  <span>Default Unit</span>
                  <input
                    type="text"
                    value={formState.unit || (formState.billing === "monthly" ? "Monthly" : "Project")}
                    onChange={(e) => update("unit", e.target.value)}
                  />
                </label>

                <label className="wide">
                  <span>Scope Description</span>
                  <textarea
                    value={formState.description || ""}
                    rows={2}
                    placeholder="What does this service include?"
                    onChange={(e) => update("description", e.target.value)}
                  />
                </label>
              </>
            )}

            {kind === "expense" && (
              <>
                {/* Quick Subscription Presets */}
                <div className="wide" style={{ gridColumn: "span 2", marginBottom: "4px" }}>
                  <div style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-faint)", marginBottom: "6px" }}>
                    Quick Repeating Subscription Presets:
                  </div>
                  <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                    {[
                      { name: "Claude Code Subscription", vendor: "Anthropic / Claude", cat: "Software", amt: 1800, rec: "monthly", mode: "Credit Card" },
                      { name: "ChatGPT Plus", vendor: "OpenAI", cat: "Software", amt: 1999, rec: "monthly", mode: "Credit Card" },
                      { name: "Vercel Pro Hosting", vendor: "Vercel Inc.", cat: "Infrastructure", amt: 1700, rec: "monthly", mode: "Credit Card" },
                      { name: "GitHub Copilot / Team", vendor: "GitHub", cat: "Software", amt: 1650, rec: "monthly", mode: "Credit Card" },
                    ].map((preset) => (
                      <button
                        key={preset.name}
                        type="button"
                        onClick={() => {
                          const today = new Date();
                          const nextMonth = new Date(today);
                          nextMonth.setMonth(nextMonth.getMonth() + 1);
                          setFormState((prev: any) => ({
                            ...prev,
                            description: preset.name,
                            vendor: preset.vendor,
                            category: preset.cat,
                            amount: preset.amt,
                            recurrence: preset.rec,
                            paymentMethod: preset.mode,
                            status: "active",
                            date: today.toISOString().slice(0, 10),
                            nextBillingDate: nextMonth.toISOString().slice(0, 10),
                          }));
                        }}
                        style={{
                          padding: "4px 8px",
                          fontSize: "11px",
                          borderRadius: "var(--radius-sm)",
                          background: "#f1f5f9",
                          border: "1px solid var(--border-subtle)",
                          cursor: "pointer",
                          fontWeight: 600,
                          color: "var(--brand-navy)",
                        }}
                      >
                        + {preset.name} (₹{preset.amt}/mo)
                      </button>
                    ))}
                  </div>
                </div>

                <label>
                  <span>Expense Date</span>
                  <input
                    type="date"
                    value={formState.date || new Date().toISOString().slice(0, 10)}
                    onChange={(e) => update("date", e.target.value)}
                    required
                  />
                </label>

                <label>
                  <span>Repeating / Cost Type</span>
                  <select
                    value={formState.recurrence || "one_time"}
                    onChange={(e) => {
                      const rec = e.target.value;
                      update("recurrence", rec);
                      if (rec !== "one_time" && !formState.nextBillingDate) {
                        const next = new Date(formState.date || new Date());
                        if (rec === "monthly") next.setMonth(next.getMonth() + 1);
                        else if (rec === "annual") next.setFullYear(next.getFullYear() + 1);
                        else if (rec === "quarterly") next.setMonth(next.getMonth() + 3);
                        update("nextBillingDate", next.toISOString().slice(0, 10));
                        update("status", "active");
                      }
                    }}
                  >
                    <option value="one_time">One-Time Expense</option>
                    <option value="monthly">🔄 Monthly Repeating Subscription (e.g. Claude Code)</option>
                    <option value="annual">📅 Annual Subscription (e.g. Domains, Cloudflare)</option>
                    <option value="quarterly">Quarterly Repeating</option>
                  </select>
                </label>

                {formState.recurrence && formState.recurrence !== "one_time" && (
                  <>
                    <label>
                      <span>Next Renewal / Billing Date</span>
                      <input
                        type="date"
                        value={formState.nextBillingDate || ""}
                        onChange={(e) => update("nextBillingDate", e.target.value)}
                      />
                    </label>

                    <label>
                      <span>Subscription Status</span>
                      <select
                        value={formState.status || "active"}
                        onChange={(e) => update("status", e.target.value)}
                      >
                        <option value="active">Active (Renewing)</option>
                        <option value="paused">Paused</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </label>
                  </>
                )}

                <label>
                  <span>Expense Category</span>
                  <select
                    value={formState.category || "Software"}
                    onChange={(e) => update("category", e.target.value)}
                  >
                    <option value="Software">Software & SaaS</option>
                    <option value="Infrastructure">Infrastructure & Cloud</option>
                    <option value="Domain & DNS">Domain & DNS</option>
                    <option value="Marketing & Ads">Marketing & Ad Spend</option>
                    <option value="Office & Operations">Office & Operations</option>
                    <option value="Salaries & Contractors">Salaries & Contractors</option>
                    <option value="Professional Services">Professional Services</option>
                    <option value="Travel & Transport">Travel & Transport</option>
                  </select>
                </label>

                <label>
                  <span>Vendor / Payee</span>
                  <input
                    type="text"
                    value={formState.vendor || ""}
                    placeholder="e.g. Anthropic, Vercel, Google Cloud"
                    onChange={(e) => update("vendor", e.target.value)}
                    required
                  />
                </label>

                <label>
                  <span>Amount (₹) *</span>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={formState.amount || 0}
                    onChange={(e) => update("amount", Number(e.target.value))}
                    required
                  />
                </label>

                <label>
                  <span>Payment Method</span>
                  <input
                    type="text"
                    value={formState.paymentMethod || "Credit Card"}
                    onChange={(e) => update("paymentMethod", e.target.value)}
                  />
                </label>

                <label className="wide">
                  <span>Description / Notes</span>
                  <textarea
                    value={formState.description || ""}
                    rows={2}
                    placeholder="Purpose of this business expense or subscription"
                    onChange={(e) => update("description", e.target.value)}
                  />
                </label>
              </>
            )}
          </div>

          <div className="modal-foot">
            <button type="button" onClick={() => setActiveForm(null)}>
              Cancel
            </button>
            <button type="submit" className="primary">
              Save {kind.charAt(0).toUpperCase() + kind.slice(1)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
